import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import ConfirmDialog from './ConfirmDialog'; // Reuse the confirmation dialog
import { useListArticleComments, useDeleteArticleComment } from '../services/appService'; // Import the hooks

// Helper to format date/time if needed (can be moved to a utils file)
const formatDate = (dateInput) => {
    if (!dateInput) return 'N/A';
    try {
        const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
        if (isNaN(date.getTime())) return 'Invalid Date';
        return date.toLocaleString(); // Simple format for now
    } catch (e) {
        console.error("[formatDate comments] Error formatting date:", dateInput, e);
        return 'Invalid Date';
    }
}

function ArticleCommentsModal({ open, onClose, appId, articleId }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);

  const listCommentsHook = useListArticleComments();
  const deleteCommentHook = useDeleteArticleComment();

  const fetchComments = useCallback(async () => {
    if (!appId || !articleId) {
      setComments([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Assume listCommentsHook takes appId and articleId
      const data = await listCommentsHook(appId, articleId);
      console.log("[ArticleCommentsModal] Fetched comments:", data);
      setComments(data || []);
    } catch (err) {
      console.error("Error fetching comments:", err);
      setError(err.message || 'Failed to load comments.');
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [appId, articleId, listCommentsHook]);

  useEffect(() => {
    if (open) {
      fetchComments();
    }
  }, [open, fetchComments]);

  const handleOpenConfirmDialog = (commentId) => {
    setCommentToDelete(commentId);
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setCommentToDelete(null);
    setConfirmDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!commentToDelete || !appId || !articleId) return;
    setLoading(true); // Optional: indicate loading during delete
    setError('');
    try {
      // Assume deleteCommentHook takes appId, articleId, commentId
      await deleteCommentHook(appId, articleId, commentToDelete);
      fetchComments(); // Refresh comments list after delete
    } catch (err) {
      console.error("Error deleting comment:", err);
      setError(err.message || 'Failed to delete comment.');
    } finally {
      handleCloseConfirmDialog();
      setLoading(false); // Reset loading state
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Comments for Article {articleId ? `(${articleId.substring(0, 8)}...)` : ''}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading && !comments.length ? (
          <CircularProgress />
        ) : comments.length === 0 && !loading ? (
            <Typography>No comments found for this article.</Typography>
        ) : (
          <List dense>
            {comments.map((comment) => (
              <ListItem key={comment.id} alignItems="flex-start">
                <ListItemText
                  primary={comment.text}
                  secondary={
                    <Typography component="span" variant="body2" color="textSecondary">
                       {/* Adjust based on your actual comment object structure */} 
                      By: {comment.user_name || 'Unknown'} on {formatDate(comment.created_at)}
                    </Typography>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end" aria-label="delete" onClick={() => handleOpenConfirmDialog(comment.id)} disabled={loading}>
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Close</Button>
      </DialogActions>

      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        onConfirm={handleDeleteConfirm}
        title="Delete Comment?"
        message={`Are you sure you want to delete this comment? This action cannot be undone.`}
      />
    </Dialog>
  );
}

ArticleCommentsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  appId: PropTypes.string,
  articleId: PropTypes.string, // Can be null initially
};

export default ArticleCommentsModal; 